// Importing necessary components and types
import { LoadingSpinner } from '../LoadingSpinner'; // Loading spinner for the loading state
import { JobList } from '../job/JobList'; // Component to display the list of jobs
import { Pagination } from '../Pagination'; // Pagination component for navigation between pages
import { AlertCircle } from 'lucide-react'; // Icon for error and empty state messages
import type { Job } from '../../types/job'; // Type definition for a job object

// Props interface to define the expected properties for the component
interface JobListSectionProps {
  jobs: Job[]; // Array of jobs to display
  page: number; // Current page number
  itemsPerPage: number; // Number of jobs to show per page
  loading: boolean; // Loading state
  error: string | null; // Error message, if any
  selectedJobId: number | null; // ID of the currently selected job
  onSelectJob: (id: number) => void; // Callback to handle job selection
  onPageChange: (page: number) => void; // Callback to handle page changes
}

// Functional component to display the job list section
export function JobListSection({
  jobs,
  page,
  itemsPerPage,
  loading,
  error,
  selectedJobId,
  onSelectJob,
  onPageChange,
}: JobListSectionProps) {
  // Display a loading spinner if the data is still loading
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <LoadingSpinner />
      </div>
    );
  }

  // Display an error message if there is an error
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center p-4 bg-accent-error/10 text-accent-error rounded-lg">
          <AlertCircle className="h-5 w-5 mr-2" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Paginate the job list based on the current page and items per page
  const paginatedJobs = jobs.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalJobs = jobs.length; // Total number of jobs

  return (
    <div className="container mx-auto px-4 py-12">
      {/* Section Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {/* Display the total number of jobs */}
          <h2 className="text-xl font-semibold text-text-primary">
            {totalJobs} offre{totalJobs !== 1 ? 's' : ''} d'emploi disponible{totalJobs !== 1 ? 's' : ''}
          </h2>
          {/* Display the current page and total pages */}
          <div className="text-text-secondary text-sm">
            Page {page} sur {Math.ceil(totalJobs / itemsPerPage)}
          </div>
        </div>
      </div>

      {/* Jobs Grid */}
      {totalJobs > 0 ? (
        <div className="space-y-8">
          {/* Render the JobList component */}
          <JobList
            jobs={paginatedJobs} // Pass paginated jobs
            onSelectJob={onSelectJob} // Handle job selection
            selectedJobId={selectedJobId} // Highlight the selected job
          />

          {/* Render pagination if there are more jobs than items per page */}
          {totalJobs > itemsPerPage && (
            <Pagination
              currentPage={page} // Current page number
              totalItems={totalJobs} // Total number of jobs
              itemsPerPage={itemsPerPage} // Items per page
              onPageChange={onPageChange} // Handle page changes
            />
          )}
        </div>
      ) : (
        // Display a message if no jobs are found
        <div className="text-center py-12 bg-background-light rounded-lg">
          <AlertCircle className="mx-auto h-12 w-12 text-text-light" />
          <h3 className="mt-4 text-lg font-medium text-text-primary">
            Aucune offre trouvée
          </h3>
          <p className="mt-2 text-text-secondary">
            Essayez de modifier vos critères de recherche
          </p>
        </div>
      )}
    </div>
  );
}
